import { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FiInfo } from 'react-icons/fi';
import './MetricHelp.css';

/** Available on hover, keyboard focus and tap; portalled out of scrolling tables. */
export default function MetricHelp({ label, children }: { label: string; children: string }) {
  const id = useId();
  const trigger = useRef<HTMLButtonElement>(null);
  const tooltip = useRef<HTMLDivElement>(null);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const pinned = useRef(false);
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<{ left: number; top: number } | null>(null);
  const cancelClose = () => clearTimeout(timer.current);
  const show = () => { cancelClose(); setOpen(true); };
  const hide = () => {
    cancelClose();
    timer.current = setTimeout(() => {
      if (!pinned.current && document.activeElement !== trigger.current) setOpen(false);
    }, 120);
  };
  useEffect(() => () => clearTimeout(timer.current), []);
  useLayoutEffect(() => {
    if (!open) return;
    const place = () => {
      if (!trigger.current || !tooltip.current) return;
      const anchor = trigger.current.getBoundingClientRect();
      if (anchor.bottom < 0 || anchor.top > window.innerHeight || anchor.right < 0 || anchor.left > window.innerWidth) {
        pinned.current = false; setOpen(false); setPosition(null); return;
      }
      const box = tooltip.current.getBoundingClientRect();
      const left = Math.max(12, Math.min(anchor.left, window.innerWidth - box.width - 12));
      const preferredTop = anchor.bottom + box.height + 20 <= window.innerHeight ? anchor.bottom + 8 : anchor.top - box.height - 8;
      const top = Math.max(12, Math.min(preferredTop, window.innerHeight - box.height - 12));
      setPosition(previous => previous?.left === left && previous?.top === top ? previous : { left, top });
    };
    place();
    // Focus and tap can scroll a trigger into view. Keep its explanation open.
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [open, children]);
  useEffect(() => {
    if (!open) return;
    const close = () => { pinned.current = false; setOpen(false); setPosition(null); };
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !trigger.current?.contains(event.target) && !tooltip.current?.contains(event.target)) close();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') { event.preventDefault(); event.stopPropagation(); close(); }
    };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape, true);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape, true);
    };
  }, [open]);
  return <>
    <button ref={trigger} type="button" className="metric-help" aria-label={`About ${label}`} aria-describedby={open ? id : undefined} aria-expanded={open}
      onMouseEnter={show} onMouseLeave={hide} onFocus={show}
      onBlur={() => { pinned.current = false; hide(); }}
      onClick={() => { cancelClose(); pinned.current = !pinned.current; setOpen(pinned.current); }}>
      <FiInfo aria-hidden="true" />
    </button>
    {open ? createPortal(<div ref={tooltip} id={id} role="tooltip" className="metric-tooltip" style={{ left: position?.left ?? 12, top: position?.top ?? 12, visibility: position ? 'visible' : 'hidden' }} onMouseEnter={cancelClose} onMouseLeave={hide}>
      <strong>{label}</strong><p>{children}</p>
    </div>, document.body) : null}
  </>;
}

export function MetricLabel({ label, help }: { label: string; help: string }) {
  const split = label.lastIndexOf(' ') + 1;
  return <span className="metric-label">{label.slice(0, split)}<span className="metric-label-end">{label.slice(split)}<MetricHelp label={label}>{help}</MetricHelp></span></span>;
}
